import {useCallback, useMemo, useState} from 'react';

import type {SocialPlatform} from 'nutshell-graphql-types';

import type {UserFacingEntity} from '../../../../../../ui/core-entity/rows/peep-rows/types';

import {EditableTextfieldContent} from '../../editable-textfield-content/editable-textfield-content';
import type {SelectMenuOption} from '../../select-menu/select-menu';

import {BaseRow} from '../base-row/base-row';
import {getBusinessCardRowToolbarActions} from '../base-row/helpers';
import {RenameableRowTitle} from '../base-row/renameable-row-title';
import {useDeleteConfirmation} from '../base-row/use-delete-confirmation';
import {useValueSaveToastTracker} from '../base-row/use-value-save-toast-tracker';

import {getUrlRowSocialIcon} from './helpers';

interface Props {
    title?: string;
    isPrimary?: boolean;
    showIsPrimaryIcon?: boolean;
    canMarkAsPrimary?: boolean;
    url: string;
    displayValue?: string;
    socialPlatform?: SocialPlatform | null;
    parentEntity: UserFacingEntity;
    dropdownOptions?: SelectMenuOption[];
    onUpdateUrl: (url: string) => Promise<any>;
    onUpdateTitle: (title: string) => Promise<any>;
    onMarkAsPrimary?: () => Promise<any>;
    onDelete: () => Promise<any>;
    successFlashKey?: string;
}

export function UrlRow(props: Props) {
    const {onUpdateUrl, showIsPrimaryIcon = true} = props;

    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [isMutatingToolbar, setIsMutatingToolbar] = useState<boolean>(false);
    const [isTitleDropdownOpen, setIsTitleDropdownOpen] = useState<boolean>(false);
    const [isCopied, setIsCopied] = useState<boolean>(false);

    const {requestDelete, isDeleteConfirmOpen, deleteConfirmationDialog, deleteAnchorRef} =
        useDeleteConfirmation(props.onDelete, setIsMutatingToolbar, 'url', props.parentEntity);

    const {valueSaveToastState, setValueSaveToastState, trackValueSaveToastForPromise} =
        useValueSaveToastTracker();

    const handleRowClick = useCallback(() => {
        if (isEditing) {
            return;
        }

        setIsEditing(true);
    }, [isEditing]);

    const handleUpdate = useCallback(
        (newValue?: string | null | undefined) => {
            const formatted = newValue ? newValue.trim() : '';

            return trackValueSaveToastForPromise(
                onUpdateUrl(formatted).then((result) => {
                    setIsEditing(false);
                    return result;
                })
            );
        },
        [onUpdateUrl, trackValueSaveToastForPromise]
    );

    const toolbarActions = useMemo(() => {
        return getBusinessCardRowToolbarActions(
            {
                canMarkAsPrimary: props.canMarkAsPrimary && !props.isPrimary,
            },
            {
                onMarkAsPrimary: props.onMarkAsPrimary,
                onEdit: () => setIsEditing(true),
                onDelete: requestDelete,
                onDeleteRef: deleteAnchorRef,
                onCopy: (isCopied: boolean) => setIsCopied(isCopied),
            },
            props.url,
            isCopied,
            setIsMutatingToolbar
        );
    }, [
        props.isPrimary,
        props.canMarkAsPrimary,
        props.onMarkAsPrimary,
        requestDelete,
        deleteAnchorRef,
        props.url,
        isCopied,
    ]);

    return (
        <>
            <BaseRow
                type='url'
                onClick={handleRowClick}
                successFlashKey={props.successFlashKey}
                customIcon={getUrlRowSocialIcon(props.socialPlatform, props.isPrimary)}
                isPrimary={showIsPrimaryIcon ? props.isPrimary : undefined}
                toolbarProps={{
                    ...toolbarActions,
                    forceShowToolbar: isDeleteConfirmOpen,
                    forceHideToolbar: isEditing || isMutatingToolbar || isTitleDropdownOpen,
                }}
            >
                <RenameableRowTitle
                    title={props.title ?? 'URL'}
                    options={props.dropdownOptions ?? []}
                    onUpdateTitle={props.onUpdateTitle}
                    saveToastState={valueSaveToastState}
                    setSaveToastState={setValueSaveToastState}
                    onDropdownOpenChange={setIsTitleDropdownOpen}
                    isCopied={isCopied}
                />
                <EditableTextfieldContent
                    value={props.url}
                    displayValue={props.displayValue ?? props.url}
                    onUpdate={handleUpdate}
                    isEditing={isEditing}
                    setIsEditing={setIsEditing}
                    placeholder='Add URL…'
                    preventHideOnBlur={true}
                    shouldLinkify={true}
                    noTopMargin={true}
                    ignorePasswordAutoFill={true}
                />
            </BaseRow>
            {deleteConfirmationDialog}
        </>
    );
}
